const FIVE_DISEASE_CERTIFICATE_LABEL = "5 โรค";
const VITAL_SIGNS_REQUIRED_MIN_AGE_YEARS = 7;
const WEIGHT_MEASUREMENT_MAX_AGE_DAYS = 90;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

function toValidDate(value) {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Calculate a patient's age in whole years from their birth date.
 * Returns null when the birth date is missing or unparseable.
 */
export function calculateAgeYears(birthDate, referenceDate = new Date()) {
    const birth = toValidDate(birthDate);
    const ref = toValidDate(referenceDate);
    if (!birth || !ref) return null;

    let age = ref.getFullYear() - birth.getFullYear();
    const monthDiff = ref.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
        age -= 1;
    }
    return age;
}

/**
 * Whether any of the selected medical certificates is one of the
 * "ใบรับรองแพทย์ 5 โรค" (work/study/driving license) fitness certificates.
 */
export function hasFiveDiseaseCertificateSelected(selectedMedicalCertificates) {
    return (selectedMedicalCertificates || []).some((certificate) =>
        (certificate?.name || "").includes(FIVE_DISEASE_CERTIFICATE_LABEL)
    );
}

/**
 * UC1: BP + pulse are required when a 5-disease certificate is selected and
 * the patient is 7 years or older. A missing birth date is treated as >= 7.
 */
export function isVitalSignsRequiredForScreening({
    birthDate,
    selectedMedicalCertificates,
    referenceDate = new Date(),
} = {}) {
    if (!hasFiveDiseaseCertificateSelected(selectedMedicalCertificates)) return false;

    const ageYears = calculateAgeYears(birthDate, referenceDate);
    return ageYears === null || ageYears >= VITAL_SIGNS_REQUIRED_MIN_AGE_YEARS;
}

/**
 * Whether the patient's last recorded weight is older than 90 days, in which
 * case the form must collect a fresh weight. A patient with no prior weight
 * measurement is not considered stale — there is nothing to refresh yet.
 */
export function isWeightMeasurementStale(weightLastMeasuredAt, referenceDate = new Date()) {
    const measuredAt = toValidDate(weightLastMeasuredAt);
    if (!measuredAt) return false;

    const ref = toValidDate(referenceDate) || new Date();
    const diffDays = (ref.getTime() - measuredAt.getTime()) / MS_PER_DAY;
    return diffDays > WEIGHT_MEASUREMENT_MAX_AGE_DAYS;
}

export function buildScreeningPayload({ patientId, formData, sessionId = null }) {
    const numericPatientId = Number(patientId);
    const medicalCertificates = (formData?.selectedMedicalCertificates || []).map(
        (certificate) => ({
            medical_certificate_id: certificate.medical_certificate_id ?? certificate.id,
        })
    );

    const parseNullableNumber = (val) => {
        if (val === "" || val === null || val === undefined) return null;
        const num = Number(val);
        return Number.isNaN(num) ? null : num;
    };

    const payload = {
        patient_id: Number.isFinite(numericPatientId) ? numericPatientId : patientId,
        main_symptom: formData?.chiefComplaint || "",
        note_to_doctor: formData?.noteToDoctor || "",
        diastolic: parseNullableNumber(formData?.diastolic),
        systolic: parseNullableNumber(formData?.systolic),
        pulse: parseNullableNumber(formData?.heartRate),
        temperature: parseNullableNumber(formData?.temperature),
        weight: parseNullableNumber(formData?.weight),
        height: parseNullableNumber(formData?.height),
        congenital_disease_details: formData?.congenitalDiseaseDetail || "ไม่มี",
        drug_allergies_details:
            formData?.drugAllergyDetail && formData.drugAllergyDetail.length > 0
                ? formData.drugAllergyDetail
                : [],
        smoking_status:
            formData?.hasSmoking === "yes" ? formData?.smokingDetail : "ไม่สูบ",
        alcohol_consumption:
            formData?.hasDrinking === "yes" ? formData?.drinkingDetail : "ไม่ดื่ม",
        is_pregnant: formData?.isPregnant === "yes",
        medical_certificates: medicalCertificates,
        selected_package_ids: (formData?.treatmentPackages || []).map(Number),
        is_lab_queue: Boolean(formData?.isLabTest),
        is_lab_result_appointment: Boolean(formData?.isLabResultAppointment),
        is_telemed_queue: Boolean(formData?.isTelemedQueue),
    };

    if (sessionId) {
        payload.session_id = sessionId;
    }

    return payload;
}
